import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { fmtEUR } from "@/lib/constants";
import { ArrowRight, TrendingDown, Zap, CheckCircle2, X } from "lucide-react";

/**
 * Comparateur Renforteo vs Intérim CHR à Lyon
 * URL : /economie-vs-interim-lyon
 *
 * Hypothèses (validées par le client) :
 *   - Intérim CHR Lyon : SMIC 12 €/h × coefficient agence 1,85 = 22,20 € TTC/h
 *   - Renforteo : taux HT du pro × 1,15 (15 % frais) + TVA 20 % sur les seuls frais
 *     (hypothèse franchise en base art. 293 B CGI pour la prestation du pro)
 *
 * Calculette 100 % client-side, sans dépendance backend.
 */

const JOB_PRESETS = [
    { key: "serveur", label: "Serveur / Serveuse", ht: 17 },
    { key: "chef_de_rang", label: "Chef de rang", ht: 22 },
    { key: "barman", label: "Barman / Barmaid", ht: 20 },
    { key: "commis", label: "Commis de cuisine", ht: 16 },
    { key: "plongeur", label: "Plongeur", ht: 17 },
    { key: "cuisinier", label: "Cuisinier", ht: 22 },
    { key: "hotesse", label: "Hôte / Hôtesse d'accueil", ht: 17 },
    { key: "manutention", label: "Manutentionnaire", ht: 17 },
];

const RENFORTEO_FEE_PCT = 0.15; // 15 %
const VAT_PCT = 0.20;
const DEFAULT_INTERIM_TTC = 22.20;

export default function InterimComparator() {
    const [jobKey, setJobKey] = useState("serveur");
    const [people, setPeople] = useState(2);
    const [hours, setHours] = useState(6);
    const [interimTtc, setInterimTtc] = useState(DEFAULT_INTERIM_TTC);
    const job = JOB_PRESETS.find((j) => j.key === jobKey);
    const [renforteoHt, setRenforteoHt] = useState(job.ht);

    useEffect(() => {
        setRenforteoHt(job.ht);
    }, [jobKey, job.ht]);

    useEffect(() => {
        // SEO meta
        document.title = "Combien économiser vs l'intérim CHR à Lyon ? Calculette Renforteo | Renforteo";
        const setMeta = (name, content, attr = "name") => {
            let el = document.querySelector(`meta[${attr}="${name}"]`);
            if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
            el.setAttribute("content", content);
        };
        setMeta("description", "Simulez votre économie en passant de l'agence d'intérim CHR à un professionnel indépendant Renforteo à Lyon : coefficient 1,85 vs 15 % de frais plateforme. Calculette instantanée, chiffres réels 2026.");
        setMeta("og:title", "Économie Renforteo vs intérim CHR Lyon — Calculette 2026 | Renforteo", "property");
        setMeta("og:description", "Combien économise un restaurateur lyonnais en passant de l'intérim CHR (coefficient 1,85) au pro indépendant Renforteo ?", "property");
        setMeta("og:type", "website", "property");
        setMeta("og:url", "https://renforteo.fr/economie-vs-interim-lyon", "property");
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) { canonical = document.createElement("link"); canonical.setAttribute("rel", "canonical"); document.head.appendChild(canonical); }
        canonical.setAttribute("href", "https://renforteo.fr/economie-vs-interim-lyon");
    }, []);

    const calc = useMemo(() => {
        const totalHours = people * hours;
        const costInterim = totalHours * interimTtc;
        // Renforteo: prestation HT + 15 % commission + 20 % VAT sur commission uniquement
        const prestationHt = totalHours * renforteoHt;
        const commissionHt = prestationHt * RENFORTEO_FEE_PCT;
        const vatOnCommission = commissionHt * VAT_PCT;
        const costRenforteo = prestationHt + commissionHt + vatOnCommission;
        const savings = costInterim - costRenforteo;
        const savingsPct = costInterim > 0 ? (savings / costInterim) * 100 : 0;
        return {
            totalHours,
            costInterim: Math.round(costInterim * 100) / 100,
            prestationHt: Math.round(prestationHt * 100) / 100,
            commissionHt: Math.round(commissionHt * 100) / 100,
            vatOnCommission: Math.round(vatOnCommission * 100) / 100,
            costRenforteo: Math.round(costRenforteo * 100) / 100,
            savings: Math.round(savings * 100) / 100,
            savingsPct: Math.round(savingsPct * 10) / 10,
        };
    }, [people, hours, interimTtc, renforteoHt]);

    return (
        <div className="min-h-screen bg-white" data-testid="interim-comparator-page">
            <Header />
            {/* HERO */}
            <section className="relative bg-[#0B192C] text-white overflow-hidden noise">
                <div className="absolute inset-0 bg-gradient-to-br from-[#0B192C] via-[#0B192C] to-[#142238]" />
                <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-20">
                    <div className="pill pill-gold mb-5">
                        <TrendingDown size={12} /> Calculette 2026 · chiffres réels Lyon
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-5">
                        Combien économisez-vous vs{" "}
                        <span className="text-[#F5A623]">l'intérim CHR à Lyon ?</span>
                    </h1>
                    <p className="text-white/75 max-w-2xl text-base md:text-lg leading-relaxed">
                        Un restaurateur lyonnais qui passe de l'agence d'intérim CHR (coefficient 1,85 sur SMIC) au professionnel indépendant Renforteo (15 % de frais plateforme, TVA franchise en base sur la prestation) économise en moyenne <b className="text-white">25 à 40 %</b>. Simulez votre cas ci-dessous.
                    </p>
                </div>
            </section>

            {/* CALCULATOR */}
            <section className="max-w-6xl mx-auto px-6 md:px-10 py-16">
                <div className="grid md:grid-cols-2 gap-8">
                    {/* INPUTS */}
                    <div className="sd-card p-6 md:p-8" data-testid="calculator-inputs">
                        <div className="overline mb-3">Votre mission</div>
                        <h2 className="font-display text-2xl mb-6 text-[#0B192C]">
                            Décrivez votre besoin
                        </h2>

                        <label className="block text-sm font-semibold mb-2">Métier</label>
                        <select
                            value={jobKey}
                            onChange={(e) => setJobKey(e.target.value)}
                            className="w-full border-2 border-slate-200 rounded-lg p-3 mb-5 focus:border-[#F5A623] outline-none"
                            data-testid="calc-job-select"
                        >
                            {JOB_PRESETS.map((j) => (
                                <option key={j.key} value={j.key}>{j.label}</option>
                            ))}
                        </select>

                        <div className="grid grid-cols-2 gap-4 mb-5">
                            <div>
                                <label className="block text-sm font-semibold mb-2">Nb de personnes</label>
                                <input
                                    type="number" min={1} max={30}
                                    value={people}
                                    onChange={(e) => setPeople(Math.max(1, parseInt(e.target.value) || 1))}
                                    className="w-full border-2 border-slate-200 rounded-lg p-3 focus:border-[#F5A623] outline-none"
                                    data-testid="calc-people-input"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold mb-2">Nb d'heures / pers.</label>
                                <input
                                    type="number" min={1} max={24}
                                    value={hours}
                                    onChange={(e) => setHours(Math.max(1, parseInt(e.target.value) || 1))}
                                    className="w-full border-2 border-slate-200 rounded-lg p-3 focus:border-[#F5A623] outline-none"
                                    data-testid="calc-hours-input"
                                />
                            </div>
                        </div>

                        <div className="mb-5">
                            <label className="flex justify-between text-sm font-semibold mb-2">
                                <span>Prix intérim CHR (€ TTC/h)</span>
                                <span className="text-[#F5A623]">{interimTtc.toFixed(2)} €/h</span>
                            </label>
                            <input
                                type="range" min={16} max={28} step={0.10}
                                value={interimTtc}
                                onChange={(e) => setInterimTtc(parseFloat(e.target.value))}
                                className="w-full accent-[#F5A623]"
                                data-testid="calc-interim-slider"
                            />
                            <div className="text-[11px] text-slate-400 mt-1">
                                Base marché Lyon CHR 2026 : SMIC brut 12 €/h × coefficient agence 1,85 ≈ 22,20 € TTC/h facturé au restaurant.
                            </div>
                        </div>

                        <div>
                            <label className="flex justify-between text-sm font-semibold mb-2">
                                <span>Taux Renforteo (€ HT/h du pro)</span>
                                <span className="text-[#F5A623]">{renforteoHt.toFixed(2)} €/h HT</span>
                            </label>
                            <input
                                type="range" min={12} max={30} step={0.50}
                                value={renforteoHt}
                                onChange={(e) => setRenforteoHt(parseFloat(e.target.value))}
                                className="w-full accent-[#F5A623]"
                                data-testid="calc-renforteo-slider"
                            />
                            <div className="text-[11px] text-slate-400 mt-1">
                                Fourchette réelle Lyon 2026 : {job.label} → 12-30 €/h HT selon expérience & qualification.
                            </div>
                        </div>
                    </div>

                    {/* OUTPUTS */}
                    <div className="space-y-4" data-testid="calculator-outputs">
                        <div className="sd-card p-6 md:p-8 border-2 border-red-100 bg-red-50/30">
                            <div className="flex items-center justify-between mb-2">
                                <div className="overline text-red-700">Agence d'intérim CHR</div>
                                <span className="text-xs text-slate-400">coefficient 1,85</span>
                            </div>
                            <div className="font-display text-4xl font-black text-red-700" data-testid="calc-interim-total">
                                {fmtEUR(calc.costInterim)}
                            </div>
                            <div className="text-sm text-slate-500 mt-1">
                                {people} pers × {hours} h × {interimTtc.toFixed(2)} € TTC = {calc.totalHours} h totales
                            </div>
                        </div>

                        <div className="sd-card p-6 md:p-8 border-2 border-[#F5A623] bg-gradient-to-br from-[#F5A623]/5 to-transparent">
                            <div className="flex items-center justify-between mb-2">
                                <div className="overline text-[#F5A623]">Renforteo (franchise en base)</div>
                                <span className="text-xs text-slate-400">15 % + TVA</span>
                            </div>
                            <div className="font-display text-4xl font-black text-[#0B192C]" data-testid="calc-renforteo-total">
                                {fmtEUR(calc.costRenforteo)}
                            </div>
                            <div className="text-xs text-slate-500 mt-3 space-y-1">
                                <div className="flex justify-between"><span>Prestation HT du pro</span><span>{fmtEUR(calc.prestationHt)}</span></div>
                                <div className="flex justify-between"><span>+ Frais Renforteo (15 %)</span><span>{fmtEUR(calc.commissionHt)}</span></div>
                                <div className="flex justify-between"><span>+ TVA sur frais (20 %)</span><span>{fmtEUR(calc.vatOnCommission)}</span></div>
                            </div>
                        </div>

                        <div className="sd-card p-6 md:p-8 bg-[#0B192C] text-white noise" data-testid="calc-savings-card">
                            <div className="overline text-[#F5A623] mb-2">Votre économie</div>
                            <div className="flex items-baseline gap-3 flex-wrap">
                                <div className="font-display text-5xl font-black text-[#F5A623]">
                                    {fmtEUR(calc.savings)}
                                </div>
                                <div className="text-xl text-white/80 font-bold">
                                    soit -{calc.savingsPct.toFixed(1)} %
                                </div>
                            </div>
                            <p className="text-sm text-white/70 mt-3">
                                Économie par rapport à l'agence d'intérim CHR classique. Zéro DPAE, zéro charge patronale, zéro requalification (contrat de prestation client ↔ pro indépendant).
                            </p>
                            <Link
                                to="/devis"
                                className="btn-gold inline-block mt-5 text-sm"
                                data-testid="calc-cta-devis"
                            >
                                Publier ma mission maintenant
                                <ArrowRight className="inline ml-2 -mr-1" size={16} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* EXPLICATIONS */}
            <section className="bg-slate-50 border-y border-slate-200">
                <div className="max-w-4xl mx-auto px-6 md:px-10 py-16">
                    <div className="overline mb-3">Comment ça marche</div>
                    <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[#0B192C] mb-8">
                        Pourquoi l'intérim CHR est structurellement 30 à 45 % plus cher à Lyon ?
                    </h2>
                    <div className="space-y-6 text-sm text-slate-700 leading-relaxed">
                        <div>
                            <div className="font-display text-xl text-[#0B192C] mb-2">1. Le coefficient d'intérim CHR</div>
                            <p>
                                Une agence d'intérim CHR à Lyon (Adecco, Randstad, Manpower, Extraphone) applique un <b>coefficient multiplicateur</b> entre le taux horaire brut du salarié et le tarif facturé au restaurant. Ce coefficient couvre : charges patronales URSSAF (~42 %), congés payés (10 %), fin de mission (~10 %), marge de l'agence (~25 %), assurance responsabilité, gestion administrative.
                            </p>
                            <p className="mt-2 bg-white border-l-4 border-red-400 pl-4 py-2 text-slate-600">
                                Exemple concret : un serveur payé SMIC brut 12 €/h est facturé <b>22,20 € TTC/h</b> au restaurant lyonnais — soit +85 % vs le brut du salarié.
                            </p>
                        </div>
                        <div>
                            <div className="font-display text-xl text-[#0B192C] mb-2">2. Le modèle Renforteo (indépendants)</div>
                            <p>
                                Sur Renforteo, le professionnel est <b>indépendant immatriculé (SIRET, KBIS ou fiche INSEE)</b>. Il facture ses honoraires HT directement au restaurateur. Renforteo prélève 15 % de frais côté entreprise, avec 20 % de TVA sur les seuls frais (la prestation du pro est majoritairement en franchise en base art. 293 B CGI, donc 0 % de TVA sur la prestation).
                            </p>
                            <p className="mt-2 bg-white border-l-4 border-[#F5A623] pl-4 py-2 text-slate-600">
                                Exemple concret : un serveur indépendant à 17 €/h HT est facturé <b>19,41 € TTC/h</b> au restaurant lyonnais (17 + 2,55 + 0,51) — soit <b>-13 % vs l'intérim CHR</b> à qualité de service équivalente.
                            </p>
                        </div>
                        <div>
                            <div className="font-display text-xl text-[#0B192C] mb-2">3. Aucune charge cachée pour le restaurateur</div>
                            <p>
                                Avec Renforteo, aucune déclaration préalable à l'embauche (DPAE), aucune charge URSSAF, aucun risque de requalification (le pro est indépendant, avec son SIRET actif). Vous recevez 2 factures : celle du pro (prestation HT) + celle de Renforteo (frais + TVA). Prêt pour votre expert-comptable.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* AVANTAGES BONUS */}
            <section className="max-w-6xl mx-auto px-6 md:px-10 py-16">
                <div className="text-center mb-10">
                    <div className="overline mb-3">Au-delà du prix</div>
                    <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[#0B192C]">
                        4 autres raisons de préférer Renforteo à l'intérim CHR
                    </h2>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                    {[
                        {
                            icon: Zap,
                            title: "Premier candidat en < 6 h à Lyon",
                            desc: "Base active de plusieurs centaines de pros indépendants à Lyon. Notification push + email + SMS immédiate. Une mission urgente (< 24 h) reçoit sa 1ère candidature en moyenne en 2 h.",
                        },
                        {
                            icon: CheckCircle2,
                            title: "Vous choisissez le profil",
                            desc: "Fini l'intérimaire imposé sans photo. Sur Renforteo, vous consultez les profils vérifiés (photo, expérience, notes, SIRET), lisez les avis de vos confrères, et sélectionnez.",
                        },
                        {
                            icon: TrendingDown,
                            title: "Notation bidirectionnelle",
                            desc: "Vous notez le pro à la fin de la mission (4 critères), le pro vous note aussi (5 critères : accueil, brief, horaires, paiement, environnement). Transparence 360°.",
                        },
                        {
                            icon: CheckCircle2,
                            title: "Facture prête pour l'expert-comptable",
                            desc: "Fin de mission = 2 factures téléchargeables (pro + plateforme), archivage automatique sur votre espace, export PDF unique pour votre gestionnaire de paie.",
                        },
                    ].map((a, i) => (
                        <div key={i} className="sd-card p-6 flex gap-4">
                            <div className="w-10 h-10 rounded-xl bg-[#F5A623]/15 text-[#F5A623] flex items-center justify-center shrink-0">
                                <a.icon size={20} />
                            </div>
                            <div>
                                <div className="font-display text-lg font-bold text-[#0B192C] mb-1">{a.title}</div>
                                <div className="text-sm text-slate-500 leading-relaxed">{a.desc}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA FINAL */}
            <section className="bg-[#0B192C] text-white noise">
                <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 text-center">
                    <div className="pill pill-gold mb-5">
                        <Zap size={12} /> Offre lancement · 0 % de frais sur votre 1ère mission
                    </div>
                    <h2 className="font-display text-3xl md:text-5xl tracking-tight mb-4">
                        Faites vos économies dès la prochaine mission.
                    </h2>
                    <p className="text-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
                        Devis TTC en 60 secondes. Aucun engagement, aucun abonnement, aucun frais de dossier. Les premières candidatures arrivent dans l'heure en journée à Lyon.
                    </p>
                    <Link to="/devis" className="btn-gold" data-testid="cta-final-devis">
                        Estimer ma mission
                        <ArrowRight className="inline ml-2 -mr-1" size={18} />
                    </Link>
                </div>
            </section>

            <Footer />
        </div>
    );
}
